import { useContext, useMemo, useState } from 'react';
import Select, { components, Props, ControlProps, OptionProps, StylesConfig } from 'react-select';
import { Trans } from '@lingui/react/macro';
import UWEFormContext from '../../../../context/UWEFormContext';
import { FormField } from '../../../../util/extractFormFieldsFromSchema';
import Icon from 'src/components/Icon';

const Option = (props: OptionProps<FormField>) => {
  const { children, ...rest } = props;

  const content = useMemo(() => {
    if (rest.data.description) {
      return (
        <>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rest.data.description}</div>
          <small>{children}</small>
        </>
      );
    }
    return children;
  }, [children, rest.data.description]);

  return <components.Option {...rest}>{content}</components.Option>;
};

const Control = ({ children, ...props }: ControlProps) => {
  return (
    <components.Control {...props}>
      <span>
        <Icon icon="Crosshair" />
      </span>
      {children}
    </components.Control>
  );
};

const CustomSelect = (props: Props) => {
  const styles: StylesConfig = {
    control: css => ({ ...css, paddingLeft: '8px' }),
  };
  return <Select {...props} components={{ Control, Option }} styles={styles} />;
};

const NavigateToFieldDropdown = () => {
  const UweFormContext = useContext(UWEFormContext);
  const [inputText, setInputText] = useState<string>('');

  const { navigateToField, formFields: availableFields = [] } = UweFormContext || {};
  if (navigateToField == null) return null;

  const options = useMemo(() => {
    // Force to write 4 chars before filtering
    if (inputText.length < 4) return [];
    const escapedInputText = inputText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedInputText, 'i');
    return availableFields.filter(field => {
      const descMatch = field.description != null ? regex.test(field.description) : false;
      const nameMatch = regex.test(field.name);
      return descMatch || nameMatch;
    });
  }, [inputText, availableFields]);

  return (
    <CustomSelect
      className="autocomplete-container autocomplete-sm"
      classNamePrefix="autocomplete"
      menuPortalTarget={document.body}
      noOptionsMessage={({ inputValue }) => (inputValue.length >= 4 ? <Trans>No matching results</Trans> : <Trans>Type to search</Trans>)}
      onChange={value => {
        const field = value as FormField;
        if (field.id) navigateToField(field.id, 'primary');
        setInputText('');
      }}
      value={null}
      options={options}
      getOptionValue={o => (o as FormField).id}
      getOptionLabel={o => (o as FormField).name}
      // Filtering is handled above
      filterOption={o => true}
      onInputChange={searchText => setInputText(searchText)}
      blurInputOnSelect
      placeholder={<Trans>Navigate to field...</Trans>}
    />
  );
};

export default NavigateToFieldDropdown;
